import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = process.env.REACT_APP_API_URL;

const memberTypeConfig = {
  senior: { label: 'Senior Member', emoji: '🧓', color: '#FF6B35', bg: '#FFF3EE' },
  family: { label: 'Family Member', emoji: '👨‍👩‍👧', color: '#4CAF50', bg: '#E8F5E9' },
  volunteer: { label: 'Volunteer', emoji: '🤝', color: '#2196F3', bg: '#E3F2FD' }
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const Profile = () => {
  const navigate = useNavigate();
  const { user, token, updateUser, logout } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [stats, setStats] = useState({ events_joined: 0, posts_created: 0, sos_triggered: 0 });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editAgeGroup, setEditAgeGroup] = useState('');
  const [editLocation, setEditLocation] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/profile`, { headers });
      setProfileData(res.data.user);
      setStats(res.data.stats);
      setEditName(res.data.user.name);
      setEditAgeGroup(res.data.user.age_group || '');
      setEditLocation(res.data.user.location || '');
    } catch (err) {
      console.error('Fetch profile error:', err);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/api/profile`, {
        name: editName.trim(),
        age_group: editAgeGroup,
        location: editLocation.trim()
      }, { headers });

      setProfileData(res.data.user);
      updateUser(res.data.user);
      setEditMode(false);
      toast.success('Profile updated successfully! ✅');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out. See you soon! 👋');
    navigate('/');
  };

  const memberConf = memberTypeConfig[profileData?.member_type] || memberTypeConfig.senior;

  const avatarColors = ['#FF6B35', '#4CAF50', '#2196F3', '#9C27B0', '#FF9800'];
  const avatarColor = profileData?.name
    ? avatarColors[profileData.name.charCodeAt(0) % avatarColors.length]
    : '#FF6B35';

  const formatMemberSince = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="page-container">
      {/* Header with avatar */}
      <div style={{
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        padding: '24px 16px 48px',
        textAlign: 'center',
        color: 'white',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button
            onClick={() => navigate('/notifications')}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer', fontSize: '18px' }}
          >
            🔔
          </button>
        </div>

        {/* Avatar */}
        <div style={{
          width: '88px',
          height: '88px',
          borderRadius: '50%',
          background: 'white',
          color: avatarColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '36px',
          fontWeight: '800',
          margin: '0 auto 12px',
          border: '4px solid rgba(255,255,255,0.5)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
        }}>
          {getInitials(profileData?.name)}
        </div>

        <h2 style={{ color: 'white', fontSize: '22px', marginBottom: '4px' }}>
          {profileData?.name || '...'}
        </h2>

        {profileData?.location && (
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginBottom: '8px' }}>
            📍 {profileData.location}
          </p>
        )}

        {/* Member type badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          background: 'rgba(255,255,255,0.2)',
          padding: '4px 14px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          {memberConf.emoji} {memberConf.label}
        </div>
      </div>

      {/* Stats bar - overlapping the header */}
      <div style={{ margin: '-24px 16px 0', position: 'relative', zIndex: 10 }}>
        <div className="card" style={{ display: 'flex', padding: '16px 8px' }}>
          {[
            { label: 'Events Joined', value: stats.events_joined, emoji: '📅' },
            { label: 'Posts Shared', value: stats.posts_created, emoji: '✍️' },
            { label: 'SOS Triggered', value: stats.sos_triggered, emoji: '🆘' }
          ].map((stat, i) => (
            <React.Fragment key={stat.label}>
              {i > 0 && <div style={{ width: '1px', background: '#E8E0D8' }} />}
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '22px', marginBottom: '2px' }}>{stat.emoji}</div>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#FF6B35' }}>{stat.value}</div>
                <div style={{ fontSize: '11px', color: '#888', lineHeight: '1.3' }}>{stat.label}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="page-content" style={{ paddingTop: '16px' }}>
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Profile Details / Edit Form */}
            <div className="card" style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h4>Profile Details</h4>
                <button
                  onClick={() => setEditMode(!editMode)}
                  style={{
                    background: editMode ? '#FFEBEE' : '#FFF3EE',
                    color: editMode ? '#D32F2F' : '#FF6B35',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Hind', 'Poppins', sans-serif"
                  }}
                >
                  {editMode ? '✕ Cancel' : '✏️ Edit'}
                </button>
              </div>

              {editMode ? (
                <div>
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Age Group</label>
                    <select
                      className="form-select"
                      value={editAgeGroup}
                      onChange={(e) => setEditAgeGroup(e.target.value)}
                    >
                      <option value="">Not specified</option>
                      <option value="50-60">50 - 60 years</option>
                      <option value="60-70">60 - 70 years</option>
                      <option value="70-80">70 - 80 years</option>
                      <option value="80+">80+ years</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      placeholder="Your city or neighbourhood"
                    />
                  </div>
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? '⏳ Saving...' : '✅ Save Changes'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { label: '👤 Name', value: profileData?.name },
                    { label: '📱 Phone', value: profileData?.phone },
                    { label: '🎂 Age Group', value: profileData?.age_group || 'Not specified' },
                    { label: '📍 Location', value: profileData?.location || 'Not specified' },
                    { label: '🏷️ Member Type', value: `${memberConf.emoji} ${memberConf.label}` },
                    { label: '📅 Member Since', value: formatMemberSince(profileData?.created_at) }
                  ].map((item) => (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#888' }}>{item.label}</span>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: '#2D2D2D', textAlign: 'right', maxWidth: '60%' }}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="card" style={{ marginBottom: '12px' }}>
              <h4 style={{ marginBottom: '12px' }}>Quick Links</h4>
              {[
                { label: '🆘 SOS & Emergency Settings', path: '/sos', color: '#F44336' },
                { label: '❤️ Health Tracker', path: '/health', color: '#4CAF50' },
                { label: '📅 My Events', path: '/events', color: '#9C27B0' },
                { label: '🔔 Notifications', path: '/notifications', color: '#FF9800' }
              ].map((link) => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px 0',
                    background: 'none',
                    border: 'none',
                    borderBottom: '1px solid #F0E8E0',
                    cursor: 'pointer',
                    fontFamily: "'Hind', 'Poppins', sans-serif',",
                    fontSize: '15px',
                    color: '#2D2D2D',
                    fontWeight: '500'
                  }}
                >
                  <span>{link.label}</span>
                  <span style={{ color: '#CCC' }}>→</span>
                </button>
              ))}

              {/* Invite Friend button */}
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'SaathiCircle',
                      text: 'Join me on SaathiCircle - the senior community network!',
                      url: window.location.origin
                    });
                  } else {
                    toast.success('Share link copied! (in production, this would share the app)');
                  }
                }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  padding: '12px 0',
                  background: 'none',
                  border: 'none',
                  borderBottom: '1px solid #F0E8E0',
                  cursor: 'pointer',
                  fontFamily: "'Hind', 'Poppins', sans-serif",
                  fontSize: '15px',
                  color: '#2D2D2D',
                  fontWeight: '500'
                }}
              >
                <span>📤 Invite a Friend</span>
                <span style={{ color: '#CCC' }}>→</span>
              </button>

              <button
                onClick={() => toast('For support, call: 1800-XXX-XXXX (free)', { icon: '📞', duration: 4000 })}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  padding: '12px 0',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: "'Hind', 'Poppins', sans-serif",
                  fontSize: '15px',
                  color: '#2D2D2D',
                  fontWeight: '500'
                }}
              >
                <span>🤝 Contact Support</span>
                <span style={{ color: '#CCC' }}>→</span>
              </button>
            </div>

            {/* App info */}
            <div className="card" style={{ marginBottom: '16px', textAlign: 'center' }}>
              <p style={{ fontSize: '13px', color: '#999' }}>SaathiCircle v1.0.0</p>
              <p style={{ fontSize: '12px', color: '#ccc', marginTop: '2px' }}>
                Friends for Every Phase of Life 🌸
              </p>
            </div>

            {/* Logout Button */}
            <button
              className="btn btn-danger"
              onClick={handleLogout}
              style={{ marginBottom: '16px' }}
            >
              🚪 Logout
            </button>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;
